import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './components/common/Notification';
import { NotificationContextProvider } from './contexts/NotificationContext';
import { AlertContextProvider } from './contexts/AlertContext';
import { useAuth } from './contexts/AuthContext';
import LoadingSpinner from './components/common/LoadingSpinner';
import Layout from './components/common/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import PrivacyPolicyPage from './components/legal/PrivacyPolicyPage';
import TermsOfServicePage from './components/legal/TermsOfServicePage';
import ResellerDashboard from './components/dashboard/ResellerDashboard';
import CardHistory from './components/dashboard/CardHistory';
import Operations from './components/dashboard/Operations';
import ProductCatalog from './components/catalog/ProductCatalog';

import AdminDashboard from './components/admin/AdminDashboard';
import UserManagement from './components/admin/UserManagement';
import ProductManagement from './components/admin/ProductManagement';
import CategoryManagement from './components/admin/CategoryManagement';
import TopupTemplateManagement from './components/admin/TopupTemplateManagement';
import TopupOrdersAdmin from './components/admin/TopupOrdersAdmin';
import UserRoleManager from './components/admin/UserRoleManager';
import NewProduct from './components/admin/NewProduct';
import EditProduct from './components/admin/EditProduct';
import AdminTransactions from './components/admin/AdminTransactions';
import UserProfile from './components/profile/UserProfile';
import UserSettings from './components/profile/UserSettings';
import NotificationTest from './components/test/NotificationTest';
import TopupHub from './components/topup/TopupHub';
import TopupServiceDetail from './components/topup/TopupServiceDetail';

// Composant pour la redirection intelligente
const DefaultRedirect = () => {
  const { currentUser, userProfile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (currentUser && userProfile) {
    // Rediriger vers le dashboard approprié selon le rôle
    if (userProfile.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }

    if (userProfile.role === 'operator') {
      return <Navigate to="/operator/topup-orders" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Utilisateur non connecté, rediriger vers login
  return <Navigate to="/login" replace />;
};

// Composant pour router vers le bon dashboard selon le rôle
const DashboardRouter = () => {
  const { currentUser, userProfile } = useAuth();
  
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  
  if (userProfile?.role === 'admin') {
    return <AdminDashboard />;
  }

  if (userProfile?.role === 'operator') {
    return <Navigate to="/operator/topup-orders" replace />;
  }
  
  return <ResellerDashboard />;
};

function App() {
  return (
    <NotificationProvider>
      <AuthProvider>
        <NotificationContextProvider>
          <AlertContextProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/" element={<DefaultRedirect />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              
              {/* Dashboard Route - Conditional based on user role */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DashboardRouter />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Reseller Routes */}
              <Route 
                path="/reseller/dashboard" 
                element={
                  <ProtectedRoute requiredRole="reseller">
                    <Layout>
                      <ResellerDashboard />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/settings" 
                element={
                  <ProtectedRoute requiredRoles={['reseller', 'operator']}>
                    <Layout>
                      <UserSettings />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/catalog" 
                element={
                  <ProtectedRoute requiredRole="reseller">
                    <Layout>
                      <ProductCatalog />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/card-history" 
                element={
                  <ProtectedRoute requiredRole="reseller">
                    <Layout>
                      <CardHistory />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/history" 
                element={
                  <ProtectedRoute requiredRole="reseller">
                    <Layout>
                      <div className="flex items-center justify-center min-h-96">
                        <div className="text-center">
                          <h2 className="text-2xl font-bold text-gray-900 mb-4">Historique des achats</h2>
                          <p className="text-gray-600">Fonctionnalité en cours de développement</p>
                        </div>
                      </div>
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/recharge" 
                element={
                  <ProtectedRoute requiredRole="reseller">
                    <Layout>
                      <div className="flex items-center justify-center min-h-96">
                        <div className="text-center">
                          <h2 className="text-2xl font-bold text-gray-900 mb-4">Recharge de solde</h2>
                          <p className="text-gray-600">Seuls les administrateurs peuvent recharger votre solde.</p>
                          <p className="text-gray-600 mt-2">Contactez un administrateur pour effectuer une recharge.</p>
                        </div>
                      </div>
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute requiredRoles={['reseller', 'operator']}>
                    <Layout>
                      <UserProfile />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Test Route */}
              <Route 
                path="/test-notifications" 
                element={
                  <ProtectedRoute requiredRole="reseller">
                    <Layout>
                      <NotificationTest />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/operations" 
                element={
                  <ProtectedRoute requiredRole="reseller">
                    <Layout>
                      <Operations />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/bill-payments"
                element={
                  <ProtectedRoute requiredRole="reseller">
                    <Navigate to="/dashboard" replace />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/topup" 
                element={
                  <ProtectedRoute requiredRole="reseller">
                    <Layout>
                      <TopupHub />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/topup/service/:productId" 
                element={
                  <ProtectedRoute requiredRole="reseller">
                    <Layout>
                      <TopupServiceDetail />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/operator/topup-orders"
                element={
                  <ProtectedRoute requiredRole="operator">
                    <Layout>
                      <TopupOrdersAdmin />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/consumption" 
                element={
                  <ProtectedRoute requiredRole="reseller">
                    <Layout>
                      <div className="flex items-center justify-center min-h-96">
                        <div className="text-center">
                          <h2 className="text-2xl font-bold text-gray-900 mb-4">Consommation</h2>
                          <p className="text-gray-600">Fonctionnalité en cours de développement</p>
                        </div>
                      </div>
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/directory" 
                element={
                  <ProtectedRoute requiredRole="reseller">
                    <Layout>
                      <div className="flex items-center justify-center min-h-96">
                        <div className="text-center">
                          <h2 className="text-2xl font-bold text-gray-900 mb-4">Annuaire</h2>
                          <p className="text-gray-600">Fonctionnalité en cours de développement</p>
                        </div>
                      </div>
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Admin Routes */}
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <AdminDashboard />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/users" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <UserManagement />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/products" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <ProductManagement />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/topup/services" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <ProductManagement />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/topup/orders" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <TopupOrdersAdmin />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/categories" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <CategoryManagement />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/topup/categories" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <CategoryManagement />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/products/new" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <NewProduct />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/topup/services/new" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <NewProduct />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/products/:id/edit" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <EditProduct />
                    </Layout>
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/admin/topup/templates" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <TopupTemplateManagement />
                    </Layout>
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/admin/transactions" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <AdminTransactions />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/admin/roles"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <UserRoleManager />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/admin/recharge" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Navigate to="/admin" replace />
                  </ProtectedRoute>
                } 
              />
              {/* 404 Page */}
              <Route 
                path="*" 
                element={
                  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                    <div className="text-center">
                      <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
                      <h2 className="text-2xl font-semibold text-gray-700 mb-4">Page non trouvée</h2>
                      <p className="text-gray-600 mb-8">La page que vous recherchez n'existe pas.</p>
                      <a 
                        href="/login" 
                        className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        Retour à l'accueil
                      </a>
                    </div>
                  </div>
                } 
              />
            </Routes>
          </div>
        </Router>
          </AlertContextProvider>
        </NotificationContextProvider>
      </AuthProvider>
    </NotificationProvider>
  );
}

export default App;
